/**
 * Translation strings for relative time formatting
 */
export interface RelativeTimeTranslations {
  editedFor?: string;        // Template: "Ändrat {{lastEdited}} sedan" / "Edited {{lastEdited}} ago"
  recently?: string;         // "Redigerad nyligen" / "Edited recently"
  dateMissing?: string;      // "Datum saknas" / "Date missing"
  invalidDate?: string;      // "Ogiltigt datum" / "Invalid date"
  day?: string;              // "dag" / "day"
  days?: string;             // "dagar" / "days"
  hour?: string;             // "timme" / "hour"
  hours?: string;            // "timmar" / "hours"
  minute?: string;           // "minut" / "minute"
  minutes?: string;          // "minuter" / "minutes"
  second?: string;           // "sekund" / "second"
  seconds?: string;          // "sekunder" / "seconds"
}

/**
 * Shared translation strings for menu actions
 */
export interface MenuTranslations {
  menuEdit?: string;
  menuDelete?: string;
  menuUpgrade?: string;
}

/**
 * Base translation interface shared by both ProjectCard and ProjectList
 */
export interface BaseProjectTranslations extends MenuTranslations {
  relativeTime?: RelativeTimeTranslations;
}

/**
 * Default Swedish translations for menu actions
 */
export const defaultMenuTranslations: MenuTranslations = {
  menuEdit: 'Redigera information',
  menuDelete: 'Radera projekt',
  menuUpgrade: 'Till team projekt'
};

/**
 * Default Swedish translations for relative time
 */
const defaultRelativeTimeTranslations: Required<RelativeTimeTranslations> = {
  editedFor: 'Ändrat {{lastEdited}} sedan',
  recently: 'Redigerad nyligen',
  dateMissing: 'Datum saknas',
  invalidDate: 'Ogiltigt datum',
  day: 'dag',
  days: 'dagar',
  hour: 'timme',
  hours: 'timmar',
  minute: 'minut',
  minutes: 'minuter',
  second: 'sekund',
  seconds: 'sekunder'
};

/**
 * Format a date string or Date object to relative time
 * @param date - Date string (ISO format) or Date object
 * @param translations - Optional translation strings (defaults to Swedish)
 * @returns Formatted relative time string (e.g., "Redigerad för 2 timmar sedan")
 */
export function formatRelativeTime(
  date: string | Date | undefined,
  translations?: RelativeTimeTranslations
): string {
  const t = { ...defaultRelativeTimeTranslations, ...translations };
  
  if (!date) {
    return t.dateMissing;
  }
  
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(then.getTime())) {
    return t.invalidDate;
  }
  
  const diffMs = now.getTime() - then.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Helper function to format the time string
  const formatTime = (value: number, singular: string, plural: string): string => {
    const timeStr = `${value} ${value > 1 ? plural : singular}`;
    // Check if editedFor has the {{lastEdited}} placeholder
    if (t.editedFor.includes('{{lastEdited}}')) {
      return t.editedFor.replace('{{lastEdited}}', timeStr);
    }
    // Fallback to old format: "Redigerad för [time] sedan"
    return `${t.editedFor} ${timeStr}`;
  };

  if (diffDays > 0) {
    return formatTime(diffDays, t.day, t.days);
  } else if (diffHours > 0) {
    return formatTime(diffHours, t.hour, t.hours);
  } else if (diffMinutes > 0) {
    return formatTime(diffMinutes, t.minute, t.minutes);
  } else if (diffSeconds > 0) {
    return formatTime(diffSeconds, t.second, t.seconds);
  } else {
    return t.recently;
  }
}

/**
 * Format a location object to a display string
 * @param location - Location string or object with city, country, etc.
 * @returns Formatted location string (e.g., "Stockholm, Sweden")
 */
export function formatLocation(
  location: string | { city?: string; country?: string; [key: string]: any } | undefined
): string {
  if (!location) return '';
  if (typeof location === 'string') return location;
  
  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.country) parts.push(location.country);
  return parts.join(', ');
}

/**
 * Translation strings for Project components (Card and List)
 */
export interface ProjectTranslations extends BaseProjectTranslations {
  clientLabel?: string;
  sizeLabel?: string;
  locationLabel?: string;
}

export interface ProjectMember {
  userId?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  id?: string;
}

export interface ProjectUser {
  id?: string;
  email?: string;
}

export interface ProjectMenuAction {
  id: string;
  label: string;
  icon: string;
}

/**
 * Finds the current user's project member record.
 * Match priority: user id, then email.
 */
export function getMatchedProjectMember(
  members: ProjectMember[] | undefined,
  user: ProjectUser | undefined
): ProjectMember | undefined {
  if (!members || !user) return undefined;

  return members.find((member) => {
    if (user.id && member.userId === user.id) return true;
    if (user.email && member.email === user.email) return true;
    return false;
  });
}

/**
 * Hides owner-restricted actions for users with owner role in members list.
 */
export function getAvailableProjectMenuActions(
  actions: ProjectMenuAction[],
  members: ProjectMember[] | undefined,
  user: ProjectUser | undefined
): ProjectMenuAction[] {
  const matchedMember = getMatchedProjectMember(members, user);
  const role = (matchedMember?.role || '').trim().toLowerCase();
  const isOwner = role === 'owner';

  const isRestrictedForNonOwner = (action: ProjectMenuAction): boolean => {
    const actionId = (action.id || '').toLowerCase();
    const actionLabel = (action.label || '').toLowerCase();

    return (
      actionId === 'remove' ||
      actionId === 'delete' ||
      actionId.includes('remove') ||
      actionId.includes('delete') ||
      actionLabel.includes('remove') ||
      actionLabel.includes('delete') ||
      actionLabel.includes('radera')
    );
  };

  // Only owners can see the remove action.
  if (isOwner) return actions;

  return actions.filter((action) => !isRestrictedForNonOwner(action));
}

/**
 * Default Swedish translations for Project components
 */
export const defaultProjectTranslations: ProjectTranslations = {
  clientLabel: 'Kund',
  sizeLabel: 'Storlek',
  locationLabel: 'Plats',
  ...defaultMenuTranslations
};

/**
 * Base props shared by ProjectCard and ProjectList components
 */
export interface BaseProjectProps {
  /** Current user used for permission checks */
  user?: ProjectUser;
  /** Unique project identifier */
  id: string;
  /** Project name */
  name: string;
  /** Client/customer name */
  client?: string;
  /** Project size */
  size?: number;
  /** Project location (string or location object with city, country, etc.) */
  location?: string | { city?: string; country?: string; streetAddress?: string; nameOfLocation?: string; [key: string]: any };
  /** Array of label tags (can be Label objects or strings) */
  labels?: any[];
  /** Last updated timestamp (ISO string or Date object) */
  updatedAt?: string | Date;
  /** Project members for role-based action filtering */
  members?: ProjectMember[];
  /** Project type: 0 = private, 1 = team, 2 = team editing */
  type?: 0 | 1 | 2;
  /** Optional project image object with url property */
  image?: { url?: string };
  /** Click handler */
  onClick?: () => void;
  /** Available menu actions */
  menuActions?: any[];
  /** Callback when menu action is triggered */
  onMenuAction?: (projectId: string, actionId: string) => void;
  /** Translation strings (defaults to Swedish) */
  translations?: ProjectTranslations;
  /** Text to display when values are not entered */
  notEntered?: string;
}
